import React, { useState } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/autoplay';
import {
  Box,
  Image,
  useTheme,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  SimpleGrid, ModalFooter, Button
} from '@chakra-ui/react';
import Icon from '../Icon';
import {css} from "@emotion/react";
import {deleteMedia, setDefaultMedia, updateMedia} from "../../endpoints/api";
import {EntityRole, useGetCurrentUserRoleForEntity} from "../../hooks/useEntityUserRole";

const EditIconContainer = styled(Box)`
  background: white;
  border-radius: 7px;
  width: 26px;
  height: 26px;
  padding: 5px;
  position: absolute;
  right: 5px;
  bottom: 5px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  z-index: 1;
  ${(props) =>
  props.theme &&
  props.theme.colors &&
  css`
      border: 1px solid ${props.theme.colors.grey};
    `};
`;

const EditOverlay = styled(Box)`
  border-radius: 50%;
  width: 100px;
  height: 100px;
  position: absolute;
`;

const iconStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '1px',
  textAlign: 'center',
  fontSize: '14px',
};

type EditImage = {
  id?: number;
  url?: string;
  file?: File;
  isDefault: boolean;
  isRemoved?: boolean;
};

interface MediaGalleryProps {
  media: any[];
  entityType: string;
  entityId: number;
  refetch: () => void;
}

const MediaEditor: React.FC<MediaGalleryProps> = ({ media, entityType, entityId, refetch }) => {
  const theme = useTheme();

  const [editImages, setEditImages] = useState<EditImage[] | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { data: currentRole } = useGetCurrentUserRoleForEntity(entityType, entityId);
  const canEdit = currentRole?.data === EntityRole.MANAGER || currentRole?.data === EntityRole.TEAM_MEMBER;

  function startEditing() {
    const mapped: EditImage[] = media.map((m) => ({
      id: m.id,
      url: m.full_url,
      isDefault: m.is_default,
    }));

    setEditImages(mapped);
    setIsEditing(true);
  }

  function setCover(index: number) {
    setEditImages((images) =>
      images!.map((img, i) => ({
        ...img,
        isDefault: i === index,
      }))
    );
  }

  function removeImage(index: number) {
    setEditImages((images) =>
      images!.map((img, i) =>
        i === index ? { ...img, isRemoved: true } : img
      )
    );
  }

  function addImages(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;

    const newImages: EditImage[] = Array.from(e.target.files).map((file) => ({
      file: file as File,
      isDefault: false,
    }));

    setEditImages((images) => [...images!, ...newImages]);
  }

  async function saveImages() {
    if (!editImages) return;

    const selectedCover = editImages.find(
      (img) => img.isDefault && !img.isRemoved
    );

    const fd = new FormData();

    const newImages = editImages.filter(
      (img) => img.file && !img.isRemoved
    );

    newImages.forEach((img) => {
      fd.append('images[]', img.file!);
    });

    if (selectedCover?.file) {
      const defaultIndex = newImages.findIndex(
        (img) => img === selectedCover
      );

      if (defaultIndex !== -1) {
        fd.append('default_image_index', String(defaultIndex));
      }
    }

    if (newImages.length > 0) {
      await updateMedia({
        entityType,
        entityId,
        formData: fd,
      }).then(() => refetch());
    }

    if (selectedCover?.id && !selectedCover.file) {
      await setDefaultMedia({
        entityType,
        entityId,
        mediaId: selectedCover.id,
      }).then(() => refetch());
    }

    let hasDeletions = false;
    for (const img of editImages) {
      if (img.id && img.isRemoved) {
        hasDeletions = true;
        await deleteMedia({
          entityType,
          entityId,
          mediaId: img.id,
        });
      }
    }

    if (hasDeletions) {
      refetch();
    }

    setIsEditing(false);
  }

  if(!canEdit) {
    return <></>;
  }


  return (
    <>
      <EditOverlay>
        <EditIconContainer top="1rem" right={media.length <= 1 ? "1rem" : "6rem"} theme={theme} onClick={() => startEditing()}>
          <Icon style={iconStyle} iconName="fi fi-rr-add-image" />
        </EditIconContainer>
      </EditOverlay>
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
        <ModalContent>
          <ModalHeader>Edit photos</ModalHeader>
          <ModalBody>
            <SimpleGrid columns={3} spacing="8px">
              {editImages?.filter(i => !i.isRemoved).map((img, index) => (
                <Box key={img.id ?? index}>
                  {(img.url || img.file) && (
                    <Image src={img.url || URL.createObjectURL(img.file!)}/>
                  )}
                  <Icon
                    iconName={img.isDefault ? 'fi-sr-star' : 'fi fi-rr-star'}
                    style={{
                      color: img.isDefault ? '#F5B700' : '#999',
                    }}
                    onClick={() => setCover(index)}
                  />
                  <Icon
                    iconName="fi fi-rr-trash"
                    onClick={() => removeImage(index)}
                  />
                </Box>
              ))}
            </SimpleGrid>

            <input
              style={{ marginTop: "3rem" }}
              type="file"
              multiple
              accept="image/*"
              onChange={addImages}
            />
          </ModalBody>

          <ModalFooter>
            <Button onClick={saveImages}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
};

export default MediaEditor;
